import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiSettingsRegional,
  usePutApiSettingsRegional,
  useDeleteApiSettingsRegional,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { ArrowLeft, Save, AlertCircle, Globe, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, TextField, Select, useToast, Modal } from '@/components/ui';

// Common currencies
const COMMON_CURRENCIES = [
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'NOK', label: 'NOK - Norwegian Krone' },
  { value: 'SEK', label: 'SEK - Swedish Krona' },
  { value: 'DKK', label: 'DKK - Danish Krone' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'OTHER', label: 'Other...' },
];

// Form schema
const regionalSettingsSchema = z.object({
  currencyCode: z
    .string()
    .min(3, 'Currency code must be 3 letters')
    .max(3, 'Currency code must be 3 letters')
    .regex(/^[A-Z]{3}$/, 'Currency code must be 3 uppercase letters'),
});

type RegionalSettingsFormData = z.infer<typeof regionalSettingsSchema>;

export function RegionalSettingsPage() {
  const { addToast } = useToast();
  const [selectedCurrency, setSelectedCurrency] = useState<string>('EUR');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useGetApiSettingsRegional();
  const settings = data?.settings;

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isDirty },
  } = useForm<RegionalSettingsFormData>({
    resolver: zodResolver(regionalSettingsSchema),
    defaultValues: {
      currencyCode: 'EUR',
    },
  });

  const isOtherSelected = selectedCurrency === 'OTHER';

  // Initialize form from API data
  useEffect(() => {
    if (settings?.currencyCode) {
      const code = settings.currencyCode;
      const isCommon = COMMON_CURRENCIES.some((c) => c.value === code && c.value !== 'OTHER');
      setSelectedCurrency(isCommon ? code : 'OTHER');
      reset({ currencyCode: code });
    }
  }, [settings, reset]);

  // Handle dropdown selection
  const handleCurrencySelect = (value: string) => {
    setSelectedCurrency(value);
    if (value !== 'OTHER') {
      setValue('currencyCode', value, { shouldDirty: true, shouldValidate: true });
    }
  };

  const { mutate: saveSettings, isPending: isSaving } = usePutApiSettingsRegional({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Regional settings saved', variant: 'success' });
        refetch();
      },
      onError: (err: Error) => {
        addToast({
          message: err.message ?? 'Failed to save settings',
          variant: 'error',
        });
      },
    },
  });

  const { mutate: clearSettings, isPending: isClearing } = useDeleteApiSettingsRegional({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Regional settings cleared', variant: 'success' });
        setIsDeleteConfirmOpen(false);
        setSelectedCurrency('EUR');
        reset({ currencyCode: 'EUR' });
        refetch();
      },
      onError: (err: Error) => {
        addToast({
          message: err.message ?? 'Failed to clear settings',
          variant: 'error',
        });
      },
    },
  });

  const onSubmit = (data: RegionalSettingsFormData) => {
    saveSettings({
      data: {
        currencyCode: data.currencyCode.toUpperCase(),
      },
    });
  };

  const handleClear = () => {
    clearSettings();
  };

  const hasSettings = settings && settings.currencyCode;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={ROUTES.SETTINGS}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Regional
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Currency and regional defaults
          </p>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load regional settings</p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      )}

      {/* Form */}
      {!isLoading && !isError && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Info callout */}
          <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-500/10">
            <Globe className="mt-0.5 h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                Currency for Resource Dashboard
              </p>
              <p className="mt-1 text-sm text-blue-600 dark:text-blue-300/80">
                The currency code is used to display costs in the Resource Dashboard. Choose your
                local currency to see accurate cost calculations.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="space-y-5">
              {/* Currency dropdown */}
              <Select
                label="Currency"
                value={selectedCurrency}
                onChange={handleCurrencySelect}
                options={COMMON_CURRENCIES}
                fullWidth
              />

              {/* Custom currency input (shown when "Other" is selected) */}
              {isOtherSelected && (
                <Controller
                  name="currencyCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Currency Code"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      error={errors.currencyCode?.message}
                      placeholder="e.g., PLN"
                      maxLength={3}
                      className="uppercase"
                    />
                  )}
                />
              )}
            </div>
          </div>

          {/* Current settings info */}
          {hasSettings && (
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-zinc-800/50">
              <div className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                Current Settings
              </div>
              <div className="mt-2 text-sm text-gray-600 dark:text-zinc-400">
                <span className="text-gray-500 dark:text-zinc-500">Currency: </span>
                {settings.currencyCode}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-6 dark:border-white/[0.06]">
            <div>
              {hasSettings && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to={ROUTES.SETTINGS}>
                <Button variant="secondary">Cancel</Button>
              </Link>
              <Button type="submit" loading={isSaving} disabled={!isDirty}>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Clear Regional Settings"
        subtitle="This will reset to defaults"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleClear} loading={isClearing}>
              Clear Settings
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Are you sure you want to clear your regional settings? You can always configure them again
          later.
        </p>
      </Modal>
    </div>
  );
}
